// Finance Expenses (2026-08-25 redesign).
//
// Same expense mutation / query surface as before; only the shell,
// header, KPI row, and empty state were rewritten onto the shared
// Components/Finance primitives so the whole Finance area reads as
// one product. The "New Expense" inline form structure and every
// input remains functionally identical.
import React, { useState, useMemo } from "react";
import { Box, Typography, Grid, Button, Chip } from "@mui/material";
import { StepUpSpinner } from "../../Components/Loader";
import {
  useGetFinanceExpensesQuery, useGetExpenseCategoriesQuery,
  useGetFinanceAccountsQuery,
} from "../../Api/attoDeskApi";
import dayjs from "dayjs";

import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import {
  FinancePageHeader, FinanceKpiCard, FinanceSectionCard, FinanceEmptyState, fin,
} from "../../Components/Finance";
import AddExpenseDialog from "./AddExpenseDialog";

const fmtMoney = (n: number) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Expenses = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: expData, isLoading, refetch } = useGetFinanceExpensesQuery({
    startDate: dayjs().subtract(3, "month").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });
  const { data: catData } = useGetExpenseCategoriesQuery();
  const { data: accData } = useGetFinanceAccountsQuery();

  const expenses: any[] = useMemo(() => (expData?.data as any)?.expenses || [], [expData]);
  const totalExpenses = Number((expData?.data as any)?.total || 0);
  const categories: any[] = useMemo(() => Array.isArray(catData?.data) ? catData!.data as any[] : [], [catData]);
  const accounts: any[] = useMemo(() => Array.isArray(accData?.data) ? accData!.data as any[] : [], [accData]);

  // Compute real, derivable KPI metrics only (spec §24 — no fake data).
  // "This month" is a client-side slice of the same expense array the
  // server already sent; not a separate request.
  const thisMonthTotal = useMemo(() => {
    const start = dayjs().startOf("month");
    return expenses
      .filter((e: any) => e.expense_date && dayjs(e.expense_date).isSame(start, "month"))
      .reduce((s: number, e: any) => s + Number(e.amount || 0), 0);
  }, [expenses]);
  const topCategory = useMemo(() => {
    const g = new Map<string, number>();
    expenses.forEach((e: any) => {
      const cat = categories.find((c: any) => c.id === e.category_id);
      const key = cat?.name || e.category || "Other";
      g.set(key, (g.get(key) || 0) + Number(e.amount || 0));
    });
    if (g.size === 0) return null;
    return Array.from(g.entries()).sort((a, b) => b[1] - a[1])[0];
  }, [expenses, categories]);

  return (
    <Box sx={{ bgcolor: fin.color.pageBg, minHeight: "100%", mx: -3, mt: -3, px: 3, pt: 3, pb: 3 }}>
      <FinancePageHeader
        title="Expenses"
        description="Track operating expenses and recent spending."
        icon={<TrendingDownIcon />}
        actions={
          <Button
            variant="contained" size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: "none", fontWeight: 800, borderRadius: 2,
              bgcolor: fin.color.blue, boxShadow: "none",
              px: 2, py: 0.75,
              "&:hover": {
                bgcolor: fin.color.navy,
                boxShadow: `0 6px 14px rgba(23,50,77,0.14)`,
              },
            }}
          >
            Add Expense
          </Button>
        }
      />

      {/* KPI row — every metric derived from the same expenses payload. */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item lg={3} md={6} sm={6} xs={12}>
          <FinanceKpiCard label="This Month" value={fmtMoney(thisMonthTotal)}
            icon={<TrendingDownIcon />} tone="danger" hint={dayjs().format("MMMM YYYY")} />
        </Grid>
        <Grid item lg={3} md={6} sm={6} xs={12}>
          <FinanceKpiCard label="Last 3 Months" value={fmtMoney(totalExpenses)}
            icon={<EventNoteOutlinedIcon />} tone="steel" hint="Total spending" />
        </Grid>
        <Grid item lg={3} md={6} sm={6} xs={12}>
          <FinanceKpiCard label="Expense Count" value={String(expenses.length)}
            icon={<ReceiptIcon />} tone="blue" hint="Entries in view" />
        </Grid>
        <Grid item lg={3} md={6} sm={6} xs={12}>
          <FinanceKpiCard
            label="Top Category"
            value={topCategory ? topCategory[0] : "—"}
            icon={<CategoryOutlinedIcon />} tone="warning"
            hint={topCategory ? fmtMoney(topCategory[1]) : "No expenses yet"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Category breakdown */}
        <Grid item lg={4} md={12} xs={12}>
          <FinanceSectionCard title="By Category" icon={<CategoryOutlinedIcon />} tone="warning"
            subtitle="Last 3 months, grouped">
            {expenses.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: fin.color.muted }}>
                Nothing to break down yet.
              </Typography>
            ) : (() => {
              const grouped = new Map<string, { total: number; color: string }>();
              expenses.forEach((e: any) => {
                const cat = categories.find((c: any) => c.id === e.category_id) || { name: e.category, color: fin.color.muted };
                const key = cat.name || e.category;
                const prev = grouped.get(key) || { total: 0, color: cat.color || fin.color.muted };
                grouped.set(key, { total: prev.total + Number(e.amount), color: prev.color });
              });
              const rows = Array.from(grouped.entries()).sort((a, b) => b[1].total - a[1].total);
              return (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {rows.map(([name, data], i) => (
                    <Box key={name} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      py: 1, borderBottom: i < rows.length - 1 ? `1px solid ${fin.color.borderSoft}` : "none",
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: data.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12.5, color: fin.color.navy, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {name}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: fin.color.dangerText, fontVariantNumeric: "tabular-nums" }}>
                        {fmtMoney(data.total)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </FinanceSectionCard>
        </Grid>

        {/* Recent Expenses */}
        <Grid item lg={8} md={12} xs={12}>
          <FinanceSectionCard title="Recent Expenses" icon={<ReceiptIcon />} tone="danger"
            subtitle={expenses.length === 0 ? "Add your first expense to get started." : `${expenses.length} entries in the last 3 months`}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><StepUpSpinner size="small" /></Box>
            ) : expenses.length === 0 ? (
              <FinanceEmptyState
                icon={<ReceiptIcon />}
                title="No expenses recorded"
                body="Expenses you add will appear here."
                tone="danger"
                cta={{ label: "Add Expense", onClick: () => setDialogOpen(true), icon: <AddIcon sx={{ fontSize: 16 }} /> }}
                minHeight={200}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {expenses.slice(0, 20).map((exp: any) => {
                  const cat = categories.find((c: any) => c.id === exp.category_id);
                  const catColor = cat?.color || fin.color.muted;
                  return (
                    <Box key={exp.id} sx={{
                      display: "flex", alignItems: "center", gap: 2, px: 1.5, py: 1.25,
                      borderRadius: `${fin.radius.sm}px`,
                      border: `1px solid ${fin.color.border}`,
                      borderLeft: `3px solid ${catColor}`,
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      "&:hover": { boxShadow: fin.shadow.card },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: fin.color.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {exp.title}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.35, flexWrap: "wrap" }}>
                          <Chip label={cat?.name || exp.category} size="small"
                            sx={{ height: 20, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", bgcolor: fin.color.surfaceBlue, color: fin.color.blue, border: `1px solid ${fin.color.border}` }} />
                          {exp.vendor && <Typography sx={{ fontSize: 11, color: fin.color.muted }}>{exp.vendor}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: fin.color.dangerText, fontVariantNumeric: "tabular-nums" }}>
                          {fmtMoney(Number(exp.amount))}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: fin.color.muted, mt: 0.15 }}>
                          {exp.expense_date ? dayjs(exp.expense_date).format("MMM D, YYYY") : ""}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </FinanceSectionCard>
        </Grid>
      </Grid>

      {/* Add Expense — modal only. The page never expands inline any
          more; opening / closing the dialog leaves the KPI row and
          Recent Expenses list visually stable behind the overlay. */}
      <AddExpenseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { refetch(); }}
        categories={categories as any}
        accounts={accounts as any}
      />
    </Box>
  );
};

export default Expenses;
